import { useMemo, useState } from 'react';
import { ArrowLeftIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { initialen, mag, sorteerOpRechten, type PersoonRij } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { usePersonen, useVerwijderOpdracht, useWijzigPersoon } from './personen.api';

/**
 * Eén opdracht: wie erop zit, en wie erbij mag.
 *
 * Iemand op een opdracht zetten kon alleen vanaf de persoon, en dat is de
 * omgekeerde weg van hoe je erover denkt: je kijkt naar een opdracht en vraagt
 * je af wie erop moet. Onder water is het dezelfde handeling — de opdrachten van
 * die persoon worden bijgewerkt — maar de vraag komt hier vandaan.
 */
export function OpdrachtPagina() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = usePersonen();
  const { user } = useAuth();
  const wijzigPersoon = useWijzigPersoon();
  const verwijderOpdracht = useVerwijderOpdracht();
  const magBeheren = mag(user?.rechten, 'persoon:manage');

  const [toevoegenOpen, setToevoegenOpen] = useState(false);
  const [fout, setFout] = useState('');

  const gever = data?.opdrachtgevers.find((g) => g.opdrachten.some((o) => o.id === id));
  const opdracht = gever?.opdrachten.find((o) => o.id === id);

  // Alleen wie meedoet: het archief hoort niet bij een lopende opdracht.
  const bezetting = useMemo(
    () =>
      sorteerOpRechten(
        (data?.personen ?? []).filter(
          (p) => !p.gearchiveerdOp && p.actief && p.opdrachten.some((o) => o.id === id),
        ),
      ),
    [data?.personen, id],
  );

  if (isLoading) return <Skeleton className="h-96 w-full max-w-3xl" />;
  if (!opdracht || !gever) {
    return (
      <div className="flex flex-col gap-4">
        <Terug />
        <p className="text-muted-foreground">Deze opdracht bestaat niet (meer).</p>
      </div>
    );
  }

  /** Toevoegen en afhalen zijn allebei "de opdrachten van deze persoon opnieuw
   *  zetten"; alleen de lijst verschilt. */
  async function zetOpdrachten(persoon: PersoonRij, opdrachtIds: string[]) {
    setFout('');
    try {
      await wijzigPersoon.mutateAsync({ id: persoon.id, opdrachtIds });
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-4">
      <Terug />

      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-heading text-2xl font-semibold">{opdracht.naam}</h1>
        <span className="text-muted-foreground">{gever.naam}</span>
      </div>

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Wie erop zit</CardTitle>
          <Button onClick={() => setToevoegenOpen(true)} size="sm" variant="outline">
            <PlusIcon data-icon="inline-start" />
            Persoon
          </Button>
        </CardHeader>
        <CardContent>
          {bezetting.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Er zit nog niemand op deze opdracht.</EmptyTitle>
              </EmptyHeader>
            </Empty>
          ) : (
            <ItemGroup className="gap-3">
              {bezetting.map((p) => (
                <Item key={p.id} variant="outline">
                  <ItemMedia>
                    <Avatar className="size-8">
                      <AvatarFallback className="text-xs">{initialen(p.naam)}</AvatarFallback>
                    </Avatar>
                  </ItemMedia>
                  <ItemContent className="min-w-0">
                    <ItemTitle>
                      <Link className="hover:underline" to={`/personen/${p.id}`}>
                        {p.naam}
                      </Link>
                    </ItemTitle>
                    <ItemDescription>
                      {[p.niveau, ...p.opdrachten.filter((o) => o.id !== id).map((o) => o.naam)]
                        .filter(Boolean)
                        .join(' · ') || 'geen niveau'}
                    </ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    {/* Alleen bij wie je mag wijzigen; anders staat er een knop
                        die op een weigering uitkomt. */}
                    {p.magWijzigen && (
                      <Button
                        aria-label={`${p.naam} van deze opdracht afhalen`}
                        className="text-muted-foreground"
                        disabled={wijzigPersoon.isPending}
                        onClick={() =>
                          void zetOpdrachten(
                            p,
                            p.opdrachten.filter((o) => o.id !== id).map((o) => o.id),
                          )
                        }
                        size="icon-sm"
                        variant="ghost"
                      >
                        <XIcon />
                      </Button>
                    )}
                  </ItemActions>
                </Item>
              ))}
            </ItemGroup>
          )}
        </CardContent>
      </Card>

      {magBeheren && (
        <Card>
          <CardHeader>
            <CardTitle>Acties</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Dezelfde knop als in de lijst, en dezelfde blokkade: de server
              weigert zolang er nog iemand op zit. */}
            <Button
              disabled={verwijderOpdracht.isPending}
              onClick={async () => {
                setFout('');
                try {
                  await verwijderOpdracht.mutateAsync(opdracht.id);
                  navigate('/personen');
                } catch (err) {
                  setFout(err instanceof ApiError ? err.message : String(err));
                }
              }}
              variant="outline"
            >
              {verwijderOpdracht.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <Trash2Icon data-icon="inline-start" />
              )}
              Opdracht verwijderen
            </Button>
          </CardContent>
        </Card>
      )}

      <ToevoegenDialog
        bezig={wijzigPersoon.isPending}
        kandidaten={(data?.personen ?? []).filter(
          (p) =>
            p.magWijzigen &&
            !p.gearchiveerdOp &&
            p.actief &&
            !p.opdrachten.some((o) => o.id === id),
        )}
        onKies={async (gekozen) => {
          for (const p of gekozen) {
            await zetOpdrachten(p, [...p.opdrachten.map((o) => o.id), opdracht.id]);
          }
          setToevoegenOpen(false);
        }}
        onSluit={() => setToevoegenOpen(false)}
        open={toevoegenOpen}
        opdrachtNaam={opdracht.naam}
      />
    </div>
  );
}

function Terug() {
  return (
    <Button asChild className="self-start" size="sm" variant="ghost">
      <Link to="/personen">
        <ArrowLeftIcon data-icon="inline-start" />
        Personen
      </Link>
    </Button>
  );
}

/**
 * Wie erbij mag. Meerdere tegelijk, want een opdracht bemensen doe je zelden per
 * persoon, en met een zoekveld erboven: de lijst is iedereen die er nog niet op
 * zit.
 */
function ToevoegenDialog({
  bezig,
  kandidaten,
  onKies,
  onSluit,
  open,
  opdrachtNaam,
}: {
  bezig: boolean;
  kandidaten: PersoonRij[];
  onKies: (personen: PersoonRij[]) => void;
  onSluit: () => void;
  open: boolean;
  opdrachtNaam: string;
}) {
  const [zoek, setZoek] = useState('');
  const [gekozen, setGekozen] = useState<string[]>([]);

  const gevonden = kandidaten.filter((p) => p.naam.toLowerCase().includes(zoek.toLowerCase()));

  function herstel() {
    setZoek('');
    setGekozen([]);
  }

  return (
    <Dialog onOpenChange={(o) => !o && onSluit()} open={open}>
      <DialogContent className="sm:max-w-md" onOpenAutoFocus={herstel}>
        <DialogHeader>
          <DialogTitle>Wie zet je op {opdrachtNaam}?</DialogTitle>
        </DialogHeader>

        <Input
          aria-label="Zoek een naam"
          autoComplete="off"
          onChange={(e) => setZoek(e.target.value)}
          placeholder="Zoek een naam"
          value={zoek}
        />

        <div className="max-h-64 overflow-y-auto rounded-md border p-1">
          {gevonden.length === 0 ? (
            <p className="text-muted-foreground p-2 text-sm">Niemand gevonden.</p>
          ) : (
            gevonden.map((p) => (
              <label
                className="hover:bg-muted/50 flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5"
                htmlFor={`kandidaat-${p.id}`}
                key={p.id}
              >
                <Checkbox
                  checked={gekozen.includes(p.id)}
                  id={`kandidaat-${p.id}`}
                  onCheckedChange={(aan) =>
                    setGekozen((was) =>
                      aan === true ? [...was, p.id] : was.filter((x) => x !== p.id),
                    )
                  }
                />
                <span>{p.naam}</span>
              </label>
            ))
          )}
        </div>

        <DialogFooter>
          <Button
            disabled={gekozen.length === 0 || bezig}
            onClick={() => onKies(kandidaten.filter((p) => gekozen.includes(p.id)))}
          >
            {bezig && <Spinner data-icon="inline-start" />}
            Toevoegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
